/*
Purpose: Modal for requesting/purchasing access to private analysis results
Shows: Detection counts, pricing, request access or purchase options
Used by: JobInlineResults, SingleOrderResults when unauthorized users try to access private results
*/
import React, { useState } from 'react';
import { FaTimes, FaLock, FaCheck, FaCar, FaUsers, FaRunning, FaEye, FaClock, FaEnvelope } from 'react-icons/fa';
import './PurchaseAccessModal.css';

const PurchaseAccessModal = ({ 
  job, 
  detectionCounts, 
  onClose, 
  onPurchase, 
  onRequestAccess,
  currentUser 
}) => {
  const [isRequestMode, setIsRequestMode] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Check if this job has a price set
  const hasPrice = job?.accessPrice && job.accessPrice > 0;
  const price = job?.accessPrice || 0;

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get evidence name
  const evidenceName = job?.evidenceName || job?.evidenceId || 'Unknown Evidence';

  // Provider display name
  const providerNames = {
    'vertex_ai_vision': 'Google Vertex AI',
    'google_video_intelligence': 'Google Video Intelligence',
    'openai': 'OpenAI',
    'aws_bedrock': 'AWS Bedrock'
  };
  const providerName = providerNames[job?.provider] || job?.provider || 'AI Service';

  const handleRequestAccess = async () => {
    if (!requestMessage.trim()) return;
    
    setSending(true);
    try {
      await onRequestAccess({
        jobId: job.id,
        message: requestMessage,
        requesterId: currentUser?.uid,
        requesterEmail: currentUser?.email,
        requesterName: currentUser?.displayName || currentUser?.email
      });
      onClose();
    } catch (error) {
      console.error('Error sending access request:', error);
      alert('Failed to send request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handlePurchase = async () => {
    try {
      await onPurchase({
        jobId: job.id,
        amount: price,
        buyerId: currentUser?.uid
      });
      onClose();
    } catch (error) {
      console.error('Error processing purchase:', error);
      alert('Purchase failed. Please try again.');
    }
  };

  return (
    <div className="purchase-modal-overlay" onClick={onClose}>
      <div className="purchase-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="purchase-modal-header">
          <FaLock className="header-lock-icon" />
          <h2>Premium Analysis Results</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="purchase-modal-content">
          {/* Metadata */}
          <div className="content-metadata">
            <div className="metadata-item">
              <span className="metadata-label">Analysis by:</span>
              <span className="metadata-value">{providerName}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Evidence:</span>
              <span className="metadata-value">{evidenceName}</span>
            </div>
            {job?.completedAt && (
              <div className="metadata-item">
                <span className="metadata-label">Completed:</span>
                <span className="metadata-value">{formatDate(job.completedAt)}</span>
              </div>
            )}
          </div>

          {/* What's Included */}
          <div className="whats-included">
            <h3>
              <FaCheck className="section-icon" />
              What's Included
            </h3>
            <div className="features-grid">
              {detectionCounts.vehicles > 0 && (
                <div className="feature-item">
                  <FaCar className="feature-icon" />
                  <div className="feature-content">
                    <span className="feature-count">{detectionCounts.vehicles}</span>
                    <span className="feature-label">Vehicle{detectionCounts.vehicles !== 1 ? 's' : ''} Detected</span>
                  </div>
                </div>
              )}
              {detectionCounts.people > 0 && (
                <div className="feature-item">
                  <FaUsers className="feature-icon" />
                  <div className="feature-content">
                    <span className="feature-count">{detectionCounts.people}</span>
                    <span className="feature-label">Person{detectionCounts.people !== 1 ? 's' : ''} Identified</span>
                  </div>
                </div>
              )}
              {detectionCounts.activities > 0 && (
                <div className="feature-item">
                  <FaRunning className="feature-icon" />
                  <div className="feature-content">
                    <span className="feature-count">{detectionCounts.activities}</span>
                    <span className="feature-label">Activit{detectionCounts.activities !== 1 ? 'ies' : 'y'} Detected</span>
                  </div>
                </div>
              )}
              {detectionCounts.text > 0 && (
                <div className="feature-item">
                  <FaEye className="feature-icon" />
                  <div className="feature-content">
                    <span className="feature-count">{detectionCounts.text}</span>
                    <span className="feature-label">Text Finding{detectionCounts.text !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}
              {detectionCounts.timeline > 0 && (
                <div className="feature-item">
                  <FaClock className="feature-icon" />
                  <div className="feature-content">
                    <span className="feature-count">{detectionCounts.timeline}</span>
                    <span className="feature-label">Timeline Event{detectionCounts.timeline !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}
              
              {/* Always show these features */}
              <div className="feature-item">
                <FaCheck className="feature-icon" />
                <div className="feature-content">
                  <span className="feature-label">Full AI Analysis Report</span>
                </div>
              </div>
              <div className="feature-item">
                <FaCheck className="feature-icon" />
                <div className="feature-content">
                  <span className="feature-label">Video Synchronized Playback</span>
                </div>
              </div>
              <div className="feature-item">
                <FaCheck className="feature-icon" />
                <div className="feature-content">
                  <span className="feature-label">Bounding Box Visualization</span>
                </div>
              </div>
            </div>
          </div>

          {/* Request Access Form (if in request mode) */}
          {isRequestMode && (
            <div className="request-access-form">
              <h3>
                <FaEnvelope className="section-icon" />
                Request Access
              </h3>
              <p className="form-description">
                Send a message to the owner explaining why you need access to these results.
              </p>
              <textarea
                className="request-message-input"
                placeholder="Enter your message to the owner..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={4}
                maxLength={500}
              />
              <div className="message-counter">
                {requestMessage.length}/500 characters
              </div>
            </div>
          )}

          {/* Locked Message */}
          {!isRequestMode && (
            <div className="content-locked-message">
              <FaLock className="lock-icon-small" />
              {hasPrice 
                ? 'Purchase access to view full analysis details' 
                : 'Request access from the owner to view full analysis details'}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="purchase-modal-actions">
          {isRequestMode ? (
            <>
              <button 
                className="btn-modal-secondary" 
                onClick={() => setIsRequestMode(false)}
                disabled={sending}
              >
                Back
              </button>
              <button 
                className="btn-send-request" 
                onClick={handleRequestAccess}
                disabled={!requestMessage.trim() || sending}
              >
                <FaEnvelope />
                {sending ? 'Sending...' : 'Send Request'}
              </button>
            </>
          ) : (
            <>
              {!hasPrice ? (
                <button 
                  className="btn-request-access-full" 
                  onClick={() => setIsRequestMode(true)}
                >
                  <FaEnvelope />
                  Request Access
                </button>
              ) : (
                <>
                  <button 
                    className="btn-modal-secondary" 
                    onClick={() => setIsRequestMode(true)}
                  >
                    <FaEnvelope />
                    Request Instead
                  </button>
                  <button 
                    className="btn-purchase-access" 
                    onClick={handlePurchase}
                  >
                    <FaLock />
                    Purchase Access
                    <span className="price-tag">${price.toFixed(2)}</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseAccessModal;

